<script lang="ts" setup>
  import { computed, onMounted, ref, watch } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { useFlag } from '../../../shared/config/flags.ts';
  import { route } from '../../../shared/config/routes.ts';
  import { StatusBanner } from '../../../shared/ui/status-banner';
  import { provideRefusal } from '../model/refusal.ts';
  import { useGameView } from '../model/useGameView.ts';
  import { useHeartbeats } from '../model/useHeartbeats.ts';
  import { useNotifications } from '../model/useNotifications.ts';
  import { viewFor } from '../model/viewFor.ts';
  import ConnectionLostBanner from './ConnectionLostBanner.vue';
  import PausedModal from './PausedModal.vue';
  import SettingsSaveFailedBanner from './SettingsSaveFailedBanner.vue';
  import { views } from './views.ts';

  const router = useRouter();
  const currentRoute = useRoute();
  const showRules = ref(currentRoute.query.showRules === 'true');

  onMounted(() => {
    if (!showRules.value) return;

    const query = { ...currentRoute.query };
    delete query.showRules;

    router.replace({ query });
  });

  const playlistMode = useFlag('playlist-mode');
  const notifications = useNotifications();
  const { gameView, phase, readRefusal, resyncView } = useGameView(notifications);
  const { refusal, refuse } = provideRefusal();
  const { gameAbandoned } = useHeartbeats(phase, notifications.status, refuse);
  watch(gameView, () => refuse(''));
  const refusedMessage = computed(() => refusal.value || readRefusal.value || notifications.refusal.value);

  watch(
    () => (gameView.value && 'gameCode' in gameView.value ? String(gameView.value.gameCode) : undefined),
    (gameCode) => {
      if (currentRoute.name !== route.game || !gameCode || gameCode === currentRoute.params.gameCode) return;
      router.replace({ params: { gameCode } });
    },
    { immediate: true },
  );

  const isChooser = computed(
    () => gameView.value?.phase === 'PLAYER_CHOOSING' && gameView.value.round.chooser?.id === gameView.value.playerId,
  );
  const isHost = computed(
    () =>
      !!gameView.value && 'hostPlayer' in gameView.value && gameView.value.hostPlayer.id === gameView.value.playerId,
  );

  const settings = computed(() =>
    gameView.value && 'phaseLengthsRange' in gameView.value ? gameView.value : undefined,
  );

  const picked = computed(() =>
    gameAbandoned.value
      ? ({ name: 'GameAbandoned', props: {} } as const)
      : viewFor(gameView.value, {
          secondsLeft: notifications.secondsLeft.value,
          showRules: showRules.value,
          modes: { playlist: playlistMode.value },
          resync: resyncView,
        }),
  );
</script>

<template>
  <div class="game">
    <StatusBanner v-if="refusedMessage">{{ refusedMessage }}</StatusBanner>
    <ConnectionLostBanner v-if="notifications.connectionLost.value" />
    <SettingsSaveFailedBanner />
    <component :is="views[picked.name]" v-bind="picked.props" />
    <PausedModal
      v-if="gameView?.paused && !gameAbandoned"
      :is-chooser="isChooser"
      :is-host="isHost"
      :reason="gameView.pauseReason"
      :settings="settings"
    />
  </div>
</template>

<style scoped>
  .game {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
    position: relative;
  }
</style>
